import React from 'react';
import { TrendingUp, Clock, Users, Activity, AlertTriangle, CheckCircle } from 'lucide-react';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { QueueingMetrics, QueueingSystemParams } from '../types';
import { QueueingCalculator } from '../utils/queueingCalculations';

interface KPIDashboardProps {
  metrics: QueueingMetrics;
  params: QueueingSystemParams;
}

interface KPICardProps {
  title: string;
  value: string;
  unit?: string;
  formula: string;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'stable';
  status?: 'good' | 'warning' | 'critical';
}

const KPICard: React.FC<KPICardProps> = ({ 
  title, 
  value, 
  unit, 
  formula, 
  icon, 
  trend, 
  status = 'good' 
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'critical': return 'border-red-500 shadow-red-500/20';
      case 'warning': return 'border-yellow-500 shadow-yellow-500/20';
      default: return 'border-primary-500 shadow-primary-500/20';
    }
  };

  const getTrendIcon = () => {
    if (!trend) return null;
    const className = trend === 'up' ? 'text-red-400' : trend === 'down' ? 'text-green-400' : 'text-gray-400';
    return <TrendingUp className={`w-4 h-4 ${className} ${trend === 'down' ? 'rotate-180' : ''}`} />;
  };

  return (
    <div className={`metric-card hover:${getStatusColor()} relative overflow-hidden`}>
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary-900/5"></div>
      
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${
              status === 'critical' ? 'bg-red-500/20 text-red-400' :
              status === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-primary-500/20 text-primary-400'
            }`}>
              {icon}
            </div>
            <div>
              <h3 className="kpi-label">{title}</h3>
            </div>
          </div>
          {getTrendIcon()}
        </div>
        
        <div className="mb-3">
          <div className="flex items-baseline space-x-2">
            <span className="kpi-value">{value}</span>
            {unit && <span className="text-sm text-gray-400 font-medium">{unit}</span>}
          </div>
        </div>
        
        <div className="formula-display bg-dark-900/50 px-3 py-2 rounded-md mt-2 border border-dark-700">
          <InlineMath math={formula} />
        </div>
      </div>
    </div>
  );
};

export const KPIDashboard: React.FC<KPIDashboardProps> = ({ metrics, params }) => {
  const getUtilizationStatus = (utilization: number): 'good' | 'warning' | 'critical' => {
    if (utilization >= 0.95) return 'critical';
    if (utilization >= 0.85) return 'warning';
    return 'good';
  };

  const getQueueTimeStatus = (queueTime: number, processTime: number): 'good' | 'warning' | 'critical' => {
    const ratio = queueTime / processTime;
    if (ratio >= 5) return 'critical';
    if (ratio >= 2) return 'warning';
    return 'good';
  };

  const kpis: KPICardProps[] = [
    {
      title: 'System Utilization',
      value: QueueingCalculator.formatPercentage(metrics.utilization),
      formula: 'u = \\frac{\\lambda}{m \\times \\mu}',
      icon: <Activity className="w-5 h-5" />,
      status: getUtilizationStatus(metrics.utilization),
      trend: metrics.utilization > 0.8 ? 'up' : metrics.utilization < 0.5 ? 'down' : 'stable'
    },
    {
      title: 'Process Time',
      value: QueueingCalculator.formatNumber(metrics.processTime),
      unit: 'min',
      formula: 't_p = \\frac{1}{\\mu}',
      icon: <Clock className="w-5 h-5" />,
      status: 'good'
    },
    {
      title: 'Queue Time',
      value: QueueingCalculator.formatNumber(metrics.queueTime),
      unit: 'min',
      formula: params.servers === 1 ? 
        't_q = \\frac{CV_a^2 + CV_p^2}{2} \\times \\frac{u}{1-u} \\times t_p' : 
        't_q = \\frac{CV_a^2 + CV_p^2}{2} \\times \\frac{u^{\\sqrt{2(m+1)}-1}}{m(1-u)} \\times t_p',
      icon: <Users className="w-5 h-5" />,
      status: getQueueTimeStatus(metrics.queueTime, metrics.processTime),
      trend: metrics.queueTime > metrics.processTime * 2 ? 'up' : 'stable'
    },
    {
      title: 'Cycle Time',
      value: QueueingCalculator.formatNumber(metrics.cycleTime),
      unit: 'min',
      formula: 'CT = t_q + t_p',
      icon: <TrendingUp className="w-5 h-5" />,
      status: getQueueTimeStatus(metrics.queueTime, metrics.processTime)
    },
    {
      title: 'Work in Process',
      value: QueueingCalculator.formatNumber(metrics.wip),
      unit: 'items',
      formula: 'WIP = \\lambda \\times CT',
      icon: <Users className="w-5 h-5" />,
      status: 'good'
    },
    {
      title: 'Queue WIP',
      value: QueueingCalculator.formatNumber(metrics.wipQueue),
      unit: 'items',
      formula: 'WIP_q = \\lambda \\times t_q',
      icon: <Users className="w-5 h-5" />,
      status: getQueueTimeStatus(metrics.queueTime, metrics.processTime)
    }
  ];

  return (
    <div className="space-y-6">
      {/* System Status Alert */}
      {metrics.error && (
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 flex items-center space-x-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-red-300">System Alert</h4>
            <p className="text-sm text-red-200">{metrics.error}</p>
          </div>
        </div>
      )}

      {/* System Health Indicator */}
      {!metrics.error && (
        <div className="bg-green-900/20 border border-green-500 rounded-lg p-4 flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-green-300">System Status: Stable</h4>
            <p className="text-sm text-green-200">
              All parameters within acceptable ranges. Utilization: {QueueingCalculator.formatPercentage(metrics.utilization)}
            </p>
          </div>
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {kpis.map((kpi, index) => (
          <KPICard key={index} {...kpi} />
        ))}
      </div>

      {/* Additional Insights */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <h3 className="text-lg font-semibold text-gray-200 mb-4">Performance Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-400">Throughput:</span>
              <span className="text-gray-200 font-mono">
                {QueueingCalculator.formatNumber(metrics.throughput * 60)} items/hour
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Traffic Intensity:</span>
              <span className="text-gray-200 font-mono">
                {QueueingCalculator.formatNumber(params.arrivalRate / params.serviceRate)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Variability Factor:</span>
              <span className="text-gray-200 font-mono">
                {QueueingCalculator.formatNumber((Math.pow(params.arrivalCV, 2) + Math.pow(params.serviceCV, 2)) / 2)}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-400">Queue Length:</span>
              <span className="text-gray-200 font-mono">
                {QueueingCalculator.formatNumber(metrics.wipQueue)} items
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Service Level:</span>
              <span className="text-gray-200 font-mono">
                {metrics.utilization < 0.95 ? '✓ Good' : '⚠ Poor'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Efficiency:</span>
              <span className="text-gray-200 font-mono">
                {QueueingCalculator.formatPercentage(metrics.processTime / metrics.cycleTime)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};